import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import ModalContainer from '@components/modals/ModalContainer';
import NoData from '@components/NoData';
import { toastSuccess } from '@components/Toast';

import UploadPresentationImages from '@features/collection/components/UploadPresentationImages';
import EditPresentationInfo from '@features/collection/components/EditPresentionInfo';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	addPresentation,
	fetchPresentationInfo,
	getPresentationLink,
	savePresentationSequence
} from '@services/presentationService';

import { printApiError, STATIC_SET_CONSTANTS } from '@utils/Util';

import { getBuyerAppBaseFrontendUrl } from '@/constants/constant';

const CollectionPresentation = ({
	isPlay,
	collectionName,
	collectionId,
	onCLoseModal,
	entityType = 'COLLECTION',
	selectedProductIds = []
}) => {
	const [collectionInfo, setCollectionInfo] = useState([]);
	const [presentationInfo, setPresentationInfo] = useState({});
	const [editModal, setEditModal] = useState(false);
	const [link, setLink] = useState('');
	const [isLinkClicked, setIsLinkClicked] = useState(false);
	const [isClickSerial, setIsClickSerial] = useState(false);
	const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
	const linkRef = useRef();
	const [presentationId, setPresentationId] = useState(null);
	const dispatch = useDispatch();

	const handleAddPresentation = async () => {
		const requestBody = {
			entityId: collectionId,
			entityType: entityType,
			orderedProductIds: selectedProductIds
		};
		const response = await addPresentation(requestBody);
		setPresentationId(response);
		return response;
	};

	useEffect(() => {
		if (isPlay) {
			handleAddPresentation();
		}
	}, [collectionId, entityType, selectedProductIds.length, isPlay]);

	const getPresentationInfo = async () => {
		try {
			dispatch(openLoader());
			if (presentationId) {
				const response = await fetchPresentationInfo(presentationId);
				setPresentationInfo({
					collectionId: collectionId,
					market: response.market,
					season: response.season,
					brand: response.brand,
					middleDocument: response.middleDocument,
					endDocument: response.endDocument,
					coverDocument: response.coverDocument,
					presentationName: response?.presentationName
				});
				setCollectionInfo(response?.productInfoForPresentationList);
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onSavePresentationSequence = async (productIds) => {
		try {
			dispatch(openLoader());
			const requestBody = {
				entityId: collectionId,
				orderedProductIds: productIds,
				entityType: entityType
			};
			const response = await savePresentationSequence(requestBody, presentationId);
			if (response?.success) {
				getPresentationInfo();
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (isPlay && presentationId) {
			getPresentationInfo();
		}
	}, [isPlay, presentationId]);

	const reorder = (list, startIndex, endIndex) => {
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);
		return result;
	};

	const onDragEnd = (e) => {
		if (!e.destination) {
			return;
		}
		const sorted = reorder(collectionInfo, e.source.index, e.destination.index);
		setCollectionInfo(sorted);
		const productIds = sorted?.map((item) => item.id);
		onSavePresentationSequence(productIds);
	};

	const handleCreatePresentation = async () => {
		try {
			dispatch(openLoader());
			const response = await getPresentationLink(presentationId);
			setIsLinkClicked(false);
			const link = getBuyerAppBaseFrontendUrl() + '/presentation?presentationId=' + response;
			setIsLinkClicked(true);
			setLink(link);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onClickSerial = () => setIsClickSerial(!isClickSerial);

	return (
		<>
			{isPlay && (
				<ModalContainer
					onCloseModal={onCLoseModal}
					className="design-view-modal presentation-play-modal"
				>
					<div className="modal-dialog" role="document">
						<div className="modal-content">
							<div className="modal-header">
								<nav className="navbar navbar-expand-lg navbar-light design-tool-header">
									<div className="toggle-menu-btn">
										<button
											className="button text serial-btn pr-0"
											onClick={onClickSerial}
										>
											<CommonImage src="/icons/up-down-arrow.svg" alt="serial" />
											<span className="text-white ml-1">Serial</span>
										</button>
									</div>
									<div className="design-selection-dropdown one-third-item">
										<div className="dropdown-btn d-flex align-items-center">
											<span className="regular-14">
												{presentationInfo?.presentationName || collectionName}
											</span>
										</div>
									</div>
									<div className="profile-and-comments one-third-item">
										<ul className="d-flex menus">
											<li>
												<div>
													<button
														className="button text size32 p-0"
														type="button"
														onClick={handleCreatePresentation}
														disabled={collectionInfo.length === 0}
													>
														<CommonImage src="/icons/copy.svg" alt="share" />
														<span className="text-white ml-2">
															Copy Presentation Link
														</span>
													</button>
													<div
														ref={linkRef}
														className={
															!isLinkClicked
																? 'copy-url-popup hidden'
																: 'copy-url-popup'
														}
													>
														<div className="copy-url">
															<p className="regular-14 gray_dark_01 mb-3 weight-500">
																Copy link to share with buyer
															</p>
															<div className="d-flex">
																<input
																	type="text"
																	autoComplete="off"
																	value={link}
																	disabled
																/>
																<button
																	className="button primary size36"
																	onClick={() => {
																		navigator.clipboard.writeText(link);
																		toastSuccess('Copied');
																		setIsLinkClicked(false);
																	}}
																>
																	Copy
																</button>
															</div>
														</div>
													</div>
												</div>
											</li>

											<li>
												<button
													type="button"
													className="button text p-0"
													data-dismiss="modal"
													aria-label="Close"
													onClick={onCLoseModal}
												>
													<CommonImage
														src="/icons/Close-white.svg"
														className="cursor-pointer"
														alt="close"
													/>
												</button>
											</li>
										</ul>
									</div>
								</nav>
							</div>
							{collectionInfo.length === 0 ? (
								<div className="no-presentaion-available">
									<NoData message={'No presentation available'} />
								</div>
							) : (
								<div className="modal-body">
									{isClickSerial && (
										<>
											<DragDropContext onDragEnd={onDragEnd}>
												<Droppable droppableId="Tbody">
													{(provided) => (
														<div
															className="dragable-design-item"
															{...provided.droppableProps}
															ref={provided.innerRef}
														>
															<div className="draggable-all">
																<div className="draggable-items-container scroll-y-label">
																	{collectionInfo?.map((item, index) => (
																		<Draggable
																			key={item.id}
																			draggableId={item.id.toString()}
																			index={index}
																		>
																			{(provided) => (
																				<div
																					className="draggable-single-item"
																					key={index}
																					ref={provided.innerRef}
																					{...provided.draggableProps}
																					{...provided.dragHandleProps}
																				>
																					<CommonImage
																						className="cursor-pointer"
																						src={
																							item?.frontImageUrl
																						}
																						isStatic={false}
																						alt="front"
																					/>
																				</div>
																			)}
																		</Draggable>
																	))}
																	{provided.placeholder}
																</div>
															</div>
														</div>
													)}
												</Droppable>
											</DragDropContext>
										</>
									)}
									<div className="modal-artboards scroll-y-label">
										<section
											className={`into-section-v2 ${
												presentationInfo?.brand?.length < 8 ? 'large' : ''
											}`}
										>
											<h1 className="brand-name-lebel">{presentationInfo?.brand}</h1>
											<div className="intro-cover">
												{presentationInfo?.coverDocument?.docUrl ? (
													<CommonImage
														src={presentationInfo?.coverDocument?.docUrl}
														alt="cover"
														className="cover-photo"
														isStatic={false}
													/>
												) : (
													<div className="cover-photo-container"></div>
												)}

												<div className="presentation-edit-category d-flex justify-content-between">
													<button
														className="button ml-3"
														onClick={() => setIsOpenUploadModal(true)}
													>
														<CommonImage
															src="/icons/edit-pencil-white.svg"
															alt="presentation"
															className="mr-1"
														/>
														Edit presentation
													</button>

													{presentationInfo?.market && presentationInfo?.season && (
														<div className="cat-info">
															<p>
																{presentationInfo?.market}:{' '}
																<span> {presentationInfo?.season}</span>
															</p>
														</div>
													)}
												</div>
											</div>
											<CommonImage
												src="/images/nitex-presentation-logo.svg"
												alt="logo"
												className="bottom-logo"
											/>
										</section>
										{presentationInfo?.middleDocument?.docUrl && (
											<div className="single-design-view-page">
												<div className="presentation-view-container">
													<div className="full-page-photo">
														<CommonImage
															src={presentationInfo?.middleDocument?.docUrl}
															alt="cover"
															isStatic={false}
														/>
													</div>
												</div>
											</div>
										)}
										{collectionInfo?.map((item) => (
											<div className="single-design-view-page" key={item.id}>
												{item?.setPackStyleFlag && (
													<div className="mb-2">
														<span
															className={`status ${
																item?.setPackStyleFlag ===
																STATIC_SET_CONSTANTS.PACK_PRODUCT
																	? 'pack'
																	: item?.setPackStyleFlag ===
																		  STATIC_SET_CONSTANTS.SET_MEMBER
																		? 'set'
																		: ''
															}`}
														>
															{item?.setPackStyleFlag ===
															STATIC_SET_CONSTANTS.PACK_PRODUCT
																? 'Pack'
																: item?.setPackStyleFlag ===
																	  STATIC_SET_CONSTANTS.SET_MEMBER
																	? 'Set'
																	: ''}
														</span>
														<span className="regular-12 weight-500 ml-2">
															{item?.setPackName}
														</span>
													</div>
												)}
												<div className="presentation-view-container">
													<div className="images-view-section">
														<div className="one-third full-photo">
															<CommonImage
																src={item.frontImageUrl}
																alt="front"
																isStatic={false}
															/>
														</div>
														<div className="one-third full-photo">
															<CommonImage
																src={item.backImageUrl}
																alt="back"
																isStatic={false}
															/>
														</div>
														<div className="one-third fabric-photo">
															<div className="single-fabric">
																<CommonImage
																	src={item.fabricImageUrl}
																	alt="fabric"
																	isStatic={false}
																/>
															</div>
															<div className="single-fabric">
																<CommonImage
																	src={item.keyDetailsImageUrl}
																	alt="keyDetails"
																	isStatic={false}
																/>
															</div>
														</div>
													</div>
													<div className="single-design-details">
														<div className="design-details-heading">
															<p>
																<span className="bold">NITEX</span> DESIGNLAB
															</p>

															<p>
																STYLE:{' '}
																<span className="bold">
																	{item?.referenceNumber}
																</span>
															</p>
														</div>
														<div className="design-details-body">
															<div className="item-details">
																<p>
																	<span>ITEM:</span>
																	<span className="bold">
																		{item.styleName}
																	</span>
																</p>
																{item?.fabric2 ? (
																	<>
																		<div className="fabric-and-weight-item d-flex">
																			<p>
																				<span>Fabric:</span>
																				<span className="bold">
																					{item.fabric1}
																				</span>
																			</p>
																			<p className="ml-3">
																				<span className="weight">
																					Weight:
																				</span>
																				<span className="bold">
																					{item.weight1}
																				</span>
																			</p>
																		</div>

																		<div className="fabric-and-weight-item d-flex">
																			<p>
																				<span>Fabric:</span>
																				<span className="bold">
																					{item.fabric2}
																				</span>
																			</p>
																			<p className="ml-3">
																				<span className="weight">
																					Weight:
																				</span>
																				<span className="bold">
																					{item.weight2}
																				</span>
																			</p>
																		</div>
																	</>
																) : (
																	<>
																		<p>
																			<span>Fabric:</span>
																			<span className="bold">
																				{item.fabric1}
																			</span>
																		</p>
																		<p>
																			<span>Weight:</span>
																			<span className="bold">
																				{item.weight1}
																			</span>
																		</p>
																	</>
																)}
															</div>
														</div>
													</div>
												</div>
											</div>
										))}
										{presentationInfo?.endDocument?.docUrl && (
											<div className="single-design-view-page">
												<div className="presentation-view-container">
													<div className="full-page-photo">
														<CommonImage
															src={presentationInfo?.endDocument?.docUrl}
															alt="cover"
															isStatic={false}
														/>
													</div>
												</div>
											</div>
										)}
										<section className="last-section-v2">
											<div className="main-slogan">
												<h3 className="create">CREATE</h3>
												<h3 className="innovate">INNOVATE</h3>
												<h3 className="collaborate">COLLABORATE</h3>
												<h3 className="protect">PROTECT</h3>
											</div>
											<div className="productionlab left">
												<CommonImage
													src="/images/design-lab.svg"
													alt="production lab"
												/>
											</div>
											<div className="productionlab right">
												<CommonImage
													src="/images/production-lab.svg"
													alt="production lab"
												/>
											</div>
											<div className="website">
												<Link to="https://nitex.com/" target="_blank">
													NITEX.COM
												</Link>
											</div>
										</section>
									</div>
								</div>
							)}
						</div>
					</div>
				</ModalContainer>
			)}
			{/* {startLoader && <TimeBasedLoader startLoader={startLoader} />} */}
			{editModal && (
				<EditPresentationInfo
					isDownLoad={editModal}
					onClose={() => setEditModal(false)}
					collectionTitle={collectionName}
					collectionId={collectionId}
					presentationInfo={presentationInfo}
					setPresentationInfo={setPresentationInfo}
					entityType={entityType}
					presentationId={presentationId}
				/>
			)}
			{isOpenUploadModal && (
				<UploadPresentationImages
					onClose={() => setIsOpenUploadModal(false)}
					title="Edit Presentation"
					isEdit={true}
					collectionInfo={{
						collectionId: collectionId,
						approvalStatus: 'APPROVED'
					}}
					callBack={getPresentationInfo}
					entityType={entityType}
					presentationId={presentationId}
				/>
			)}
		</>
	);
};

export default CollectionPresentation;
